import { format } from 'date-fns';

export type AttemptAnswer = {
  answer: string;
  correct: boolean;
};

type BackendScoreTime = {
  date: string;
};

type ScoreTime = Omit<BackendScoreTime, 'timezone_type'> & {
  
};

type BackendAttempt = {
  score: number;
  max_score: number;
  spent_time: number;
  scored_at: BackendScoreTime;
  answers: AttemptAnswer[];
  scored_session_id?: string;
};

export type Attempt = Omit<BackendAttempt, 'max_score' | 'spent_time' | 'scored_at'> & {
  maxScore: number;
  spentTime: number;
  scoredAt: ScoreTime;
};

type QuestionSetAnswers = {
  last: QuestionSetAttempt;
  first: QuestionSetAttempt;
  best: QuestionSetAttempt;
};

export type QuestionSetAttempt = {
  score: number;
  maxScore: number;
  spentTime: number;
  scoredAt: ScoreTime;
  questions: Record<string, QuestionSetAttemptQuestion>;
  scored_session_id?: string;
};

export type QuestionSetAttemptQuestion = {
  title: string;
  answers?: AttemptAnswer[];
};

type BackendQuestionSetAnswers = {
  best: BackendAttempt;
  block_id: string;
  created_at: string;
  first: BackendAttempt;
  last: BackendAttempt;
  num_attempts: number;
  object_id: string;
  parent_id: string;
  student_id: string;
  title: string;
  updated_at: string;
};

export type BackendUserBlockAnswers = {
  object_id: string;
  student_id: string;
  num_attempts: number;
  first: BackendAttempt;
  best: BackendAttempt;
  last: BackendAttempt;
  updated_at: string;
  created_at: string;
  question_set_id: string;
  question_set_answers: {
    data: BackendQuestionSetAnswers[];
  };
  exercise_url: string;
};

export type UserBlockAnswers = {
  studentId: string;
  numAttempts?: number;
  attempts?: {
    last?: Attempt;
    best?: Attempt;
    first?: Attempt;
  };
  questionSetId?: string;
  questionSetAnswers?: QuestionSetAnswers;
  exerciseUrl: string;
};

// MAP FUNCTIONS
const mapBackendScoreTimeToScoreTime = (data: BackendScoreTime): ScoreTime => ({
  date: format(new Date(data.date), 'dd/MM/yyyy')
});

const mapBackendAttemptToAttempt = (data: BackendAttempt): Attempt => ({
  score: data.score,
  maxScore: data.max_score,
  spentTime: data.spent_time,
  scoredAt: mapBackendScoreTimeToScoreTime(data.scored_at),
  answers: data.answers,
  scored_session_id: data.scored_session_id
});

const mapBackendQuestionSetAnswersToQuestionSetAnswers = (
  data: BackendQuestionSetAnswers[],
  last: Attempt,
  first: Attempt,
  best: Attempt
): QuestionSetAnswers => {
  const results: QuestionSetAnswers = {
    last: {
      score: last.score,
      maxScore: last.maxScore,
      spentTime: last.spentTime,
      scoredAt: last.scoredAt,
      questions: {},
      scored_session_id: last.scored_session_id
    },
    first: {
      score: first.score,
      maxScore: first.maxScore,
      spentTime: first.spentTime,
      scoredAt: first.scoredAt,
      questions: {},
      scored_session_id: first.scored_session_id
    },
    best: {
      score: best.score,
      maxScore: best.maxScore,
      spentTime: best.spentTime,
      scoredAt: best.scoredAt,
      questions: {},
      scored_session_id: best.scored_session_id
    },
  };

  data.forEach((item: BackendQuestionSetAnswers, index) => {
    results.last.questions[index] = {
      title: item.title,
      answers: item.last.answers,
    };
    results.first.questions[index] = {
      title: item.title,
      answers: item.first.answers,
    };
    results.best.questions[index] = {
      title: item.title,
      answers: item.best.answers,
    };
  });

  return results;
};

export const mapBackendUserBlockAnswersToUserBlockAnswers = (
  data: BackendUserBlockAnswers
): UserBlockAnswers | undefined => {
  if (!data) return undefined;
  const last: Attempt | undefined = data.last ? mapBackendAttemptToAttempt(data.last) : undefined;
  const first: Attempt | undefined = data.first
    ? mapBackendAttemptToAttempt(data.first)
    : undefined;
  const best: Attempt | undefined = data.best ? mapBackendAttemptToAttempt(data.best) : undefined;

  const getAttempts = () => {
    if (last && first && best) {
      return {last, first, best}
    }
    return undefined
  }

  return {
    studentId: data.student_id,
    numAttempts: data.num_attempts,
    attempts: getAttempts(),
    questionSetId: data.question_set_id,
    questionSetAnswers:
      last && first && best
        ? mapBackendQuestionSetAnswersToQuestionSetAnswers(
            data.question_set_answers.data,
            last,
            first,
            best
          )
        : undefined,
    exerciseUrl: data.exercise_url
  };
};

// TYPE GUARD
export const isBackendUserBlockAnswers = (test: any): test is BackendUserBlockAnswers =>
  !!(test as BackendUserBlockAnswers).num_attempts;

// MAPPERS
export const getUserPageAnswersMapper = (response: any): UserBlockAnswers | undefined =>
    mapBackendUserBlockAnswersToUserBlockAnswers(response.data);

export const getGroupPageAnswersMapper = (response: any): UserBlockAnswers[] =>
  response.data.map(mapBackendUserBlockAnswersToUserBlockAnswers);
